import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { fieldOperationsService } from '../../services/fieldOperationsService';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Wrench,
  Filter
} from 'lucide-react';

const FieldOperations = () => {
  const [fieldOperations, setFieldOperations] = useState([]);

  // Load field operations from API only
  const loadFieldOperations = async () => {
    try {
      const response = await fieldOperationsService.getFieldOperations();
      if (response && response.success && Array.isArray(response.data)) {
        setFieldOperations(response.data);
      } else {
        throw new Error(response?.message || 'Failed to load field operations');
      }
    } catch (error) {
      console.warn('Failed to load field operations from API:', error.message);
      setFieldOperations([]);
    }
  };

  useEffect(() => {
    loadFieldOperations();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

  // Field types (UI only)
  const fieldTypes = [
    'Land configuration',
    'Planting type',
    'Spacing',
    'RDF',
    'Plant protection'
  ];

  const generateCode = () => {
    const maxCode = Math.max(
      ...fieldOperations.map(op => {
        const n = parseInt(String(op.code || '').replace(/\D/g, ''));
        return isNaN(n) ? 0 : n;
      }),
      0
    );
    const nextNumber = maxCode + 1;
    return `CU${nextNumber.toString().padStart(3, '0')}`;
  };

  const filteredOperations = (Array.isArray(fieldOperations) ? fieldOperations : []).filter(operation => {
    if (!operation) return false;
    const codeValue = (operation.code ?? '').toString();
    const nameValue = (operation.name ?? '').toString();
    const matchesSearch = codeValue.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          nameValue.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || operation.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      const operationData = {
        code: generateCode(), // Auto-generate code
        name: data.name,
        status: data.status || 'active'
      };

      const response = await fieldOperationsService.createFieldOperation(operationData);
      if (response.success) {
        await loadFieldOperations(); // Reload data from API
        toast.success('Field operation added successfully!');
        setShowAddModal(false);
        reset();
      } else {
        throw new Error(response.message || 'Failed to add field operation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to add field operation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      const operationData = {
        code: editingOperation.code, // Keep existing code, don't allow editing
        name: data.name,
        status: data.status
      };

      const response = await fieldOperationsService.updateFieldOperation(editingOperation.id, operationData);
      if (response.success) {
        await loadFieldOperations(); // Reload data from API
        toast.success('Field operation updated successfully!');
        setShowAddModal(false);
        setEditingOperation(null);
        reset();
      } else {
        throw new Error(response.message || 'Failed to update field operation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to update field operation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (operationId) => {
    if (!window.confirm('Are you sure you want to delete this field operation?')) return;

    setIsLoading(true);
    try {
      const response = await fieldOperationsService.deleteFieldOperation(operationId);
      if (response.success) {
        await loadFieldOperations(); // Reload data from API
        toast.success('Field operation deleted successfully!');
        return;
      } else {
        throw new Error(response.message || 'Failed to delete field operation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to delete field operation');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (operation) => {
    setEditingOperation(operation);
    setValue('code', operation.code || '');
    setValue('name', operation.name || '');
    setValue('status', operation.status || 'active');
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cultural Operation Management</h1>
          <p className="text-gray-600">Manage cultural operation types for agricultural trials</p>
        </div>
        <button 
          onClick={() => {
            setEditingOperation(null);
            reset();
            // Pre-fill with auto-generated code
            setValue('code', generateCode());
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center mt-4 sm:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Cultural Operation
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search cultural operations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Field Operations Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOperations.map((operation) => (
                <tr key={operation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-semibold text-blue-600">{operation.code ?? '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <Wrench className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="text-sm font-medium text-gray-900">{operation.name ?? '—'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(operation.status)}`}>
                      {operation.status ?? '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(operation)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(operation.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-700">
          Showing 1 to {filteredOperations.length} of {filteredOperations.length} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-700">
            Page 1 of 1
          </span>
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 {editingOperation ? 'Edit' : 'Add'} Cultural Operation
              </h2>
              <form onSubmit={handleSubmit(editingOperation ? handleEdit : handleAdd)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    {...register('code', { required: 'Code is required' })}
                    className="input-field bg-gray-100 cursor-not-allowed"
                    placeholder="e.g., CU001, CU002"
                    maxLength={10}
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editingOperation ? 'Code cannot be edited' : 'Code will be generated automatically'}
                  </p>
                  {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Enter cultural operation name"
                    className="input-field"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="active" defaultValue>
                      Active
                    </option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingOperation(null);
                      reset();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : (editingOperation ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldOperations; 